import { useMemo, useState } from 'react';
import { useParams } from 'react-router';
import ActionRollupSummary from 'components/IAP/ActionRollupSummary';
import ActionDrawer from 'components/IAP/ActionDrawer';
import ActionViewModal from 'components/IAP/ActionViewModal';
import { INCIDENT, ORGANIZATION } from 'components/IAP/ICSConstants';
import {
  useIncidentActionRollup,
  useOrgActionRollup,
} from 'components/IAP/hooks/actionRollupHooks';
import { useIncidentActions } from 'components/IAP/hooks/incidentActionHooks';
import { useOrgActions } from 'components/IAP/hooks/orgActionHooks';
import type { ActionEntity } from 'components/IAP/hooks/useScopedActionForm';
import { cn } from '@/lib/utils';
import { useSitStatAorScope } from './SitStatAorScopeContext';
import { filterSitStatTasks, isSitStatTaskDone } from './sitstat-task-model';

function formatTargetDate(value: Date | string | null | undefined) {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString();
}

/**
 * SitStat Actions widget — the same roll-up the Actions tab shows above its
 * table, then the tasks themselves. A task opens the read-only task modal, and
 * Edit there opens the task drawer.
 *
 * SitStat renders in both the Hub and an incident, so the scope follows the
 * route the way the Notifications widget does. Inside an incident the breakdown
 * is by ICS section, which is the question a section chief asks; at org level
 * there are no positions to group by, so it is by assignee.
 */
export function SitStatActionsWidget() {
  const { incidentId } = useParams<{ incidentId: string }>();
  const { scope } = useSitStatAorScope();
  const aorIds = scope?.ids ?? null;
  const [viewAction, setViewAction] = useState<ActionEntity | null>(null);
  const [editAction, setEditAction] = useState<ActionEntity | null>(null);

  // Both are instantiated unconditionally for the rules of hooks; the unused
  // side stays inert because each is `enabled`-gated on the route.
  const incidentRollup = useIncidentActionRollup(
    incidentId ?? null,
    !!incidentId,
    aorIds
  );
  const orgRollup = useOrgActionRollup(!incidentId, aorIds);
  const { data, isLoading } = incidentId ? incidentRollup : orgRollup;

  // `useIncidentActions` is gated on an incident id; the org list is the Hub's.
  const { data: incidentActions = [] } = useIncidentActions(
    INCIDENT,
    incidentId ?? null
  );
  const { data: orgActions = [] } = useOrgActions();
  const tasks = useMemo<ActionEntity[]>(
    () =>
      filterSitStatTasks<ActionEntity>(
        incidentId ? incidentActions : orgActions,
        scope
      ),
    [incidentActions, incidentId, orgActions, scope]
  );
  const scopeName = incidentId ? INCIDENT : ORGANIZATION;

  return (
    <div className="tw:space-y-3">
      <ActionRollupSummary
        rollup={data}
        isLoading={isLoading}
        groupingKey={incidentId ? 'section' : 'assignee'}
      />
      {tasks.length === 0 ? (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          {scope ? 'No tasks in this AOR.' : 'No tasks.'}
        </p>
      ) : (
        <ul
          className="list-unstyled tw:m-0 tw:max-h-72 tw:w-full tw:list-none tw:!space-y-1.5 tw:overflow-y-auto tw:!p-0 tw:!pl-0 tw:pr-0.5"
          aria-label="Tasks"
        >
          {tasks.map((task) => {
            const target = formatTargetDate(task.target_date);
            return (
              <li key={task.action_id}>
                <button
                  type="button"
                  onClick={() => setViewAction(task)}
                  className={cn(
                    'tw:w-full tw:cursor-pointer tw:rounded-md tw:border tw:border-border/60 tw:bg-transparent tw:px-2 tw:py-1.5 tw:text-left tw:text-foreground tw:hover:bg-accent/40',
                    isSitStatTaskDone(task) && 'tw:opacity-60'
                  )}
                >
                  <span className="tw:block tw:truncate tw:text-xs tw:font-medium">
                    {task.name || 'Untitled task'}
                  </span>
                  <span className="tw:block tw:truncate tw:text-[11px] tw:text-muted-foreground">
                    {[
                      task.status || 'Planned',
                      task.priority,
                      target ? `Due ${target}` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <ActionViewModal
        show={!!viewAction}
        action={viewAction}
        onClose={() => setViewAction(null)}
        canEdit
        onEdit={(action) => setEditAction(action)}
      />
      {editAction ? (
        <ActionDrawer
          scope={scopeName}
          incidentIdOverride={incidentId ?? undefined}
          selectedEntity={editAction}
          toggle={() => setEditAction(null)}
        />
      ) : null}
    </div>
  );
}
